import type { FeedControllerSnapshot } from '../core/controller';
import { compareSortOrders } from '../core/ids';
import { isRecord } from '../core/record';
import type { ChannelId, ChatItem, SortOrder, WorkspaceId } from '../core/types';
import type { ScrollState } from '../engine';

const DEFAULT_THROTTLE_MS = 1_500;

/** Source that caused the read cursor to advance. */
export type ReadCursorVia = 'channel-open' | 'bottom-reach' | 'focus' | 'blur';

/** Payload sent to the legacy read-cursor API bridge. */
export interface ReadCursorSendInput {
  readonly channelId: ChannelId;
  readonly profileId: string;
  readonly sortOrder: SortOrder;
  readonly via: ReadCursorVia;
  readonly workspaceId: WorkspaceId;
}

/** Sends a read-cursor advance, optionally asynchronously. */
export type ReadCursorSender = (input: ReadCursorSendInput) => void | Promise<void>;

/** Injectable clock, focus, timer, and sender dependencies for the read-cursor controller. */
export interface FeedReadCursorControllerOptions {
  readonly clearTimer?: (timer: ReturnType<typeof setTimeout>) => void;
  readonly hasFocus?: () => boolean;
  readonly now?: () => number;
  readonly send?: ReadCursorSender;
  readonly setTimer?: (handler: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  readonly throttleMs?: number;
}

/** Current feed/window state needed to decide whether a read-cursor advance is legal. */
export interface ReadCursorContext {
  readonly channelId: ChannelId | null;
  readonly controllerSnapshot: Pick<FeedControllerSnapshot, 'edgeState' | 'newerCursor'>;
  readonly items: readonly ChatItem[];
  readonly profileId: string | null;
  readonly scrollState?: Pick<ScrollState, 'atBottom'>;
  readonly workspaceId: WorkspaceId | null;
}

interface BottomReachState {
  channelId: ChannelId | null;
  lastSentAt: number;
  pendingSortOrder: SortOrder | null;
  timerId: ReturnType<typeof setTimeout> | null;
}

/** Stateful read-cursor policy ported from the legacy message list. */
export class FeedReadCursorController {
  private readonly clearTimer: (timer: ReturnType<typeof setTimeout>) => void;
  private readonly hasFocus: () => boolean;
  private readonly now: () => number;
  private readonly send: ReadCursorSender;
  private readonly setTimer: (handler: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  private readonly throttleMs: number;
  private channelOpenChannelId: ChannelId | null = null;
  private channelOpenSent = false;
  private readonly lastSentByChannel = new Map<ChannelId, SortOrder>();
  private bottomReachState: BottomReachState = {
    channelId: null,
    lastSentAt: 0,
    pendingSortOrder: null,
    timerId: null
  };

  constructor(options: FeedReadCursorControllerOptions = {}) {
    // Browsers require timer functions invoked with window (or detached); unbound defaults throw Illegal invocation.
    this.clearTimer = options.clearTimer ?? ((timer) => clearTimeout(timer));
    this.hasFocus = options.hasFocus ?? defaultHasFocus;
    this.now = options.now ?? Date.now;
    this.send = options.send ?? sendAdvanceReadCursor;
    this.setTimer = options.setTimer ?? ((handler, delayMs) => setTimeout(handler, delayMs));
    this.throttleMs = options.throttleMs ?? DEFAULT_THROTTLE_MS;
  }

  handleChannelOpen(context: ReadCursorContext): void {
    this.trackChannelOpenVisit(context.channelId);
    if (!isSendableContext(context)) return;
    if (!isLiveBottomWindow(context.controllerSnapshot)) return;
    if (this.channelOpenSent) return;

    const sortOrder = readCursorTargetFromItems(context.items);
    if (!sortOrder) return;

    this.channelOpenSent = true;
    this.sendForward({
      channelId: context.channelId,
      profileId: context.profileId,
      sortOrder,
      via: 'channel-open',
      workspaceId: context.workspaceId
    });
  }

  handleBottomReach(context: ReadCursorContext): void {
    if (!isSendableContext(context)) return;
    if (!isLiveBottomWindow(context.controllerSnapshot)) return;
    if (!context.scrollState?.atBottom) return;
    if (!this.hasFocus()) return;

    const sortOrder = readCursorTargetFromItems(context.items);
    if (!sortOrder) return;

    const state = this.stateForChannel(context.channelId);
    if (!state.pendingSortOrder || compareSortOrders(sortOrder, state.pendingSortOrder) > 0) {
      state.pendingSortOrder = sortOrder;
    }

    const fire = () => {
      state.timerId = null;
      const pendingSortOrder = state.pendingSortOrder;
      state.pendingSortOrder = null;
      if (!pendingSortOrder || !this.hasFocus()) return;

      if (this.sendForward({
        channelId: context.channelId,
        profileId: context.profileId,
        sortOrder: pendingSortOrder,
        via: 'bottom-reach',
        workspaceId: context.workspaceId
      })) {
        state.lastSentAt = this.now();
      }
    };

    const remainingMs = this.throttleMs - (this.now() - state.lastSentAt);
    if (remainingMs <= 0) {
      this.clearBottomTimer(state);
      fire();
      return;
    }

    if (!state.timerId) {
      state.timerId = this.setTimer(fire, remainingMs);
    }
  }

  handleWindowEvent(context: ReadCursorContext, via: Extract<ReadCursorVia, 'focus' | 'blur'>): void {
    if (!isSendableContext(context)) return;
    if (!isLiveBottomWindow(context.controllerSnapshot)) return;

    const sortOrder = readCursorTargetFromItems(context.items);
    if (!sortOrder) return;

    this.sendForward({
      channelId: context.channelId,
      profileId: context.profileId,
      sortOrder,
      via,
      workspaceId: context.workspaceId
    });
  }

  dispose(): void {
    this.clearBottomTimer(this.bottomReachState);
  }

  private stateForChannel(channelId: ChannelId): BottomReachState {
    if (this.bottomReachState.channelId === channelId) return this.bottomReachState;

    this.clearBottomTimer(this.bottomReachState);
    this.bottomReachState = {
      channelId,
      lastSentAt: 0,
      pendingSortOrder: null,
      timerId: null
    };
    return this.bottomReachState;
  }

  private clearBottomTimer(state: BottomReachState): void {
    if (!state.timerId) return;
    this.clearTimer(state.timerId);
    state.timerId = null;
  }

  private trackChannelOpenVisit(channelId: ChannelId | null): void {
    if (this.channelOpenChannelId === channelId) return;

    this.channelOpenChannelId = channelId;
    this.channelOpenSent = false;
  }

  private sendForward(input: ReadCursorSendInput): boolean {
    const lastSent = this.lastSentByChannel.get(input.channelId);
    if (lastSent && compareSortOrders(input.sortOrder, lastSent) <= 0) return false;

    this.lastSentByChannel.set(input.channelId, input.sortOrder);
    const result = this.send(input);
    if (result) void result.catch(reportReadCursorError);
    return true;
  }
}

/** Returns the newest sent, non-optimistic item sort order from rendered items. */
export function readCursorTargetFromItems(items: readonly ChatItem[]): SortOrder | null {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (!item?.sortOrder) continue;
    if (item.status !== 'sent') continue;
    if (isOptimisticRendererRow(item.rendererRow)) continue;
    return item.sortOrder;
  }

  return null;
}

/** Returns true when the active window is live-contiguous at the bottom. */
export function isLiveBottomWindow(
  snapshot: Pick<FeedControllerSnapshot, 'edgeState' | 'newerCursor'>
): boolean {
  return snapshot.edgeState.bottom === 'live' && !snapshot.newerCursor;
}

function isSendableContext(context: ReadCursorContext): context is ReadCursorContext & {
  readonly channelId: ChannelId;
  readonly profileId: string;
  readonly workspaceId: WorkspaceId;
} {
  return Boolean(context.workspaceId && context.channelId && context.profileId);
}

function isOptimisticRendererRow(rendererRow: unknown): boolean {
  return isRecord(rendererRow) && rendererRow.pseudotype === 'optimistic';
}

function defaultHasFocus(): boolean {
  return typeof document === 'undefined' ? true : document.hasFocus();
}

function sendAdvanceReadCursor(input: ReadCursorSendInput): void {
  void import('../../lib/read_cursor').then((module) => {
    // SAFETY: The legacy JS module exports advanceReadCursor with this positional contract.
    const send = module.advanceReadCursor as unknown as (
      workspaceId: WorkspaceId,
      channelId: ChannelId,
      profileId: string,
      via: ReadCursorVia,
      options: { readonly forceNetwork: true; readonly sortOrder: SortOrder }
    ) => unknown;
    send(input.workspaceId, input.channelId, input.profileId, input.via, {
      sortOrder: input.sortOrder,
      forceNetwork: true
    });
  }).catch(reportReadCursorError);
}

function reportReadCursorError(error: unknown): void {
  console.error('[MessageFeed] read cursor failed:', error);
}
